import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Step, Steps} from 'bulk-workflows/BulkWorkflowComponents';
import SaveLocationPicker from 'molbio/components/SaveLocationPicker';
import EntityStore from 'stores/EntityStore';
import {faded} from 'styles/Utilities';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GenericEvent, InputChangeEvent} from 'util/Types';
import {WorklistFormType, WorklistSummary} from 'worklists/WorklistConstants';

import {
  runAssembly,
  setFragmentFolderId,
  setFragmentWorklistFormType,
  setFragmentWorklistId,
  setNewFragmentWorklistInfo,
  setNewPrimerWorklistInfo,
  setNewSequenceWorklistInfo,
  setPrimerFolderId,
  setPrimerWorklistFormType,
  setPrimerWorklistId,
  setSequenceFolderId,
  setSequenceWorklistFormType,
  setSequenceWorklistId,
  setUseFragmentWorklist,
  setUsePrimerWorklist,
  setUseSequenceWorklist,
} from '../BulkAssemblyActions';
import {BULK_ASSEMBLY_STATE, BulkAssemblyOutputType, LocalAssembly} from '../BulkAssemblyConstants';
import * as BulkAssemblyUtil from '../BulkAssemblyUtil';
import {NavButtonStyles, NavigationPreviousButton} from './NavigationButtons';

function AssemblyRunner({assembly}: {assembly: LocalAssembly}): JSX.Element {
  const _getUseWorklistChangeHandler = (outputType: BulkAssemblyOutputType) => (useWorklist: boolean) => {
    switch (outputType) {
      case BulkAssemblyOutputType.GIBSON_PRIMERS:
        setUsePrimerWorklist(useWorklist);
        break;
      case BulkAssemblyOutputType.FRAGMENTS:
        setUseFragmentWorklist(useWorklist);
        break;
      default:
        setUseSequenceWorklist(useWorklist);
    }
  };
  const _getNewOrExistingChangeHandler =
    (outputType: BulkAssemblyOutputType) => (newOrExisting: WorklistFormType) => {
      switch (outputType) {
        case BulkAssemblyOutputType.GIBSON_PRIMERS:
          setPrimerWorklistFormType(newOrExisting);
          break;
        case BulkAssemblyOutputType.FRAGMENTS:
          setFragmentWorklistFormType(newOrExisting);
          break;
        default:
          setSequenceWorklistFormType(newOrExisting);
      }
    };
  const newWorklistOwnerId = EntityStore.currentUser().id;
  const _getNewWorklistNameChangeHandler = (outputType: BulkAssemblyOutputType) => (e: InputChangeEvent) => {
    switch (outputType) {
      case BulkAssemblyOutputType.GIBSON_PRIMERS:
        setNewPrimerWorklistInfo({name: e.target.value});
        break;
      case BulkAssemblyOutputType.FRAGMENTS:
        setNewFragmentWorklistInfo({name: e.target.value});
        break;
      default:
        setNewSequenceWorklistInfo({name: e.target.value});
    }
  };
  const _getWorklistIdChangeHandler =
    (outputType: BulkAssemblyOutputType) => (worklistSummary: WorklistSummary | null) => {
      const newId = worklistSummary ? worklistSummary.id : null;
      switch (outputType) {
        case BulkAssemblyOutputType.GIBSON_PRIMERS:
          setPrimerWorklistId(newId);
          break;
        case BulkAssemblyOutputType.FRAGMENTS:
          setFragmentWorklistId(newId);
          break;
        default:
          setSequenceWorklistId(newId);
      }
    };

  const errors = BulkAssemblyUtil.getErrors(assembly);
  const canRun =
    errors.length === 0 &&
    assembly.meta.state === BULK_ASSEMBLY_STATE.VALIDATED &&
    BulkAssemblyUtil.slotsWithGibsonPrimerErrors(assembly).length === 0;
  const _getFolderChangeHandler =
    (outputType: BulkAssemblyOutputType) =>
    (e: GenericEvent): void => {
      switch (outputType) {
        case BulkAssemblyOutputType.GIBSON_PRIMERS:
          setPrimerFolderId(e.target.value);
          break;
        case BulkAssemblyOutputType.FRAGMENTS:
          setFragmentFolderId(e.target.value);
          break;
        default:
          setSequenceFolderId(e.target.value);
      }
    };

  return (
    <div>
      <Steps>
        <Step text="Add sequences to a folder and/or worklist">
          <SaveLocationPicker
            targetFolderId={assembly.sequenceSaveInformation.targetFolderId}
            tagSchemaInfo={null}
            onTargetFolderIdChange={_getFolderChangeHandler(BulkAssemblyOutputType.ASSEMBLED_SEQUENCES)}
            worklistParams={{
              useWorklistCheckboxLabel: 'Add sequences to a worklist',
              newWorklistOwnerId,
              pluralEntityLabel: 'sequences',
              shouldShowOverflowWarningForExistingWorklist: false,
              shouldShowOverflowWarningForNewWorklist: false,
            }}
            uniqueId={BulkAssemblyOutputType.ASSEMBLED_SEQUENCES}
            useWorklist={assembly.sequenceSaveInformation.worklistParams != null}
            onUseWorklistChange={_getUseWorklistChangeHandler(BulkAssemblyOutputType.ASSEMBLED_SEQUENCES)}
            worklistFormType={
              assembly.sequenceSaveInformation.worklistParams?.worklistFormType ?? WorklistFormType.EXISTING
            }
            onWorklistFormTypeChange={_getNewOrExistingChangeHandler(
              BulkAssemblyOutputType.ASSEMBLED_SEQUENCES
            )}
            newWorklistName={assembly.sequenceSaveInformation.worklistParams?.newWorklistInfo?.name ?? ''}
            onNewWorklistNameChange={_getNewWorklistNameChangeHandler(
              BulkAssemblyOutputType.ASSEMBLED_SEQUENCES
            )}
            onWorklistIdChange={_getWorklistIdChangeHandler(BulkAssemblyOutputType.ASSEMBLED_SEQUENCES)}
          />
        </Step>
        {BulkAssemblyUtil.isGibsonAssembly(assembly) && (
          <>
            <Step text="Add primers to a folder and/or worklist">
              <SaveLocationPicker
                targetFolderId={assembly.primerSaveInformation.targetFolderId}
                tagSchemaInfo={null}
                onTargetFolderIdChange={_getFolderChangeHandler(BulkAssemblyOutputType.GIBSON_PRIMERS)}
                worklistParams={{
                  useWorklistCheckboxLabel: 'Add primers to a worklist',
                  newWorklistOwnerId,
                  pluralEntityLabel: 'primers',
                  shouldShowOverflowWarningForNewWorklist: false,
                  shouldShowOverflowWarningForExistingWorklist: false,
                }}
                uniqueId={BulkAssemblyOutputType.GIBSON_PRIMERS}
                useWorklist={assembly.primerSaveInformation.worklistParams != null}
                onUseWorklistChange={_getUseWorklistChangeHandler(BulkAssemblyOutputType.GIBSON_PRIMERS)}
                worklistFormType={
                  assembly.primerSaveInformation.worklistParams?.worklistFormType ?? WorklistFormType.EXISTING
                }
                onWorklistFormTypeChange={_getNewOrExistingChangeHandler(
                  BulkAssemblyOutputType.GIBSON_PRIMERS
                )}
                newWorklistName={assembly.primerSaveInformation.worklistParams?.newWorklistInfo?.name ?? ''}
                onNewWorklistNameChange={_getNewWorklistNameChangeHandler(
                  BulkAssemblyOutputType.GIBSON_PRIMERS
                )}
                onWorklistIdChange={_getWorklistIdChangeHandler(BulkAssemblyOutputType.GIBSON_PRIMERS)}
              />
            </Step>
            <Step text="Add fragments to a folder and/or worklist (optional)">
              <SaveLocationPicker
                targetFolderId={assembly.fragmentSaveInformation.targetFolderId}
                tagSchemaInfo={null}
                onTargetFolderIdChange={_getFolderChangeHandler(BulkAssemblyOutputType.FRAGMENTS)}
                worklistParams={{
                  useWorklistCheckboxLabel: 'Add fragments to a worklist',
                  newWorklistOwnerId,
                  pluralEntityLabel: 'fragments',
                  shouldShowOverflowWarningForExistingWorklist: false,
                  shouldShowOverflowWarningForNewWorklist: false,
                }}
                uniqueId={BulkAssemblyOutputType.FRAGMENTS}
                useWorklist={assembly.fragmentSaveInformation.worklistParams != null}
                onUseWorklistChange={_getUseWorklistChangeHandler(BulkAssemblyOutputType.FRAGMENTS)}
                worklistFormType={
                  assembly.fragmentSaveInformation.worklistParams?.worklistFormType ??
                  WorklistFormType.EXISTING
                }
                onWorklistFormTypeChange={_getNewOrExistingChangeHandler(BulkAssemblyOutputType.FRAGMENTS)}
                newWorklistName={assembly.fragmentSaveInformation.worklistParams?.newWorklistInfo?.name ?? ''}
                onNewWorklistNameChange={_getNewWorklistNameChangeHandler(BulkAssemblyOutputType.FRAGMENTS)}
                onWorklistIdChange={_getWorklistIdChangeHandler(BulkAssemblyOutputType.FRAGMENTS)}
                required={false}
              />
            </Step>
          </>
        )}
        <div className={css(styles.helpText)}>
          Trying to select a schema? Check the top of the page near the topology selector.
        </div>
      </Steps>
      <div className={css(NavButtonStyles.btnWrapper)}>
        <NavigationPreviousButton assembly={assembly} />
        <div className={css(NavButtonStyles.btn)}>
          <Button
            disabled={!canRun}
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={runAssembly}
            text="Assemble"
            dataTestAttrs={{component: 'AssemblyRunner', element: 'assembleButton'}}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  helpText: faded,
});

export default AssemblyRunner;
